import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { differenceInDays, format, addDays, eachDayOfInterval, isWithinInterval, parseISO } from 'date-fns';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import MockStripePayment from '../components/MockStripePayment';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Logo from '../components/Logo';

const { FiMapPin, FiUsers, FiCheck, FiStar, FiArrowLeft, FiInfo, FiLock, FiCalendar, FiCreditCard, FiBriefcase, FiCopy } = FiIcons;

const PublicBookingPage = () => {
  const { clientId, venueId } = useParams();
  const [step, setStep] = useState(1);
  
  // Date States
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  
  const [guests, setGuests] = useState(1);
  const [guestDetails, setGuestDetails] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    specialRequests: ''
  });
  const [venue, setVenue] = useState(null);
  const [pricingBreakdown, setPricingBreakdown] = useState(null);
  
  // Payment Method State
  const [paymentMethod, setPaymentMethod] = useState('card'); // 'card' or 'bank'
  const [bankDetails, setBankDetails] = useState(null);

  useEffect(() => {
    // In a real app, you would fetch: /api/venues/${clientId}/${venueId}
    const fetchVenue = async () => {
      await new Promise(resolve => setTimeout(resolve, 600));
      const mockVenue = {
        id: venueId,
        ownerId: clientId, 
        name: 'Luxury Downtown Apartment',
        description: 'Experience the heart of the city in this stunning modern apartment.',
        type: 'Apartment',
        location: {
          address: '123 Main St, New York, NY'
        },
        photos: [
          'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1200',
          'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800'
        ],
        facilities: ['High-Speed WiFi', 'Full Kitchen', 'Smart TV', 'Air Conditioning'],
        pricePerNight: 150,
        rating: 4.9,
        reviews: 124,
        maxGuests: 4,
        priceIncludesUtilities: false,
        selectedUtilities: ['electricity', 'cleaning'],
        utilities: [
          {
            id: 'electricity',
            name: 'Electricity',
            priceType: 'per_day',
            sameAllYear: false,
            periods: [
              { startDate: '2024-01-01', endDate: '2024-05-31', costPerDay: 5 },
              { startDate: '2024-06-01', endDate: '2024-08-31', costPerDay: 8 },
              { startDate: '2024-09-01', endDate: '2024-12-31', costPerDay: 5 }
            ]
          },
          {
            id: 'cleaning',
            name: 'Cleaning Fee',
            priceType: 'per_booking',
            sameAllYear: true,
            periods: [
              { startDate: '2024-01-01', endDate: '2024-12-31', costPerDay: 45 }
            ]
          }
        ]
      };
      setVenue(mockVenue);
      
      setBankDetails({
        accountHolder: 'Venue Management Ltd.',
        bankName: 'Chase Bank',
        iban: 'US12 3456 7890 1234 5678',
        swift: 'CHASUS33XXX',
        instructions: 'Please include your Last Name in the transfer description.'
      });
    };
    fetchVenue();
  }, [venueId, clientId]);

  useEffect(() => {
    if (startDate && endDate && venue) {
      calculatePrice();
    }
  }, [startDate, endDate, venue, guests]);

  const calculatePrice = () => {
    if (!startDate || !endDate) return;

    const nights = differenceInDays(endDate, startDate);
    if (nights <= 0) return;

    const daysInterval = eachDayOfInterval({
      start: startDate,
      end: addDays(endDate, -1)
    });

    let basePriceTotal = nights * venue.pricePerNight;
    let utilitiesTotal = 0;
    let breakdownItems = [];

    breakdownItems.push({
      label: `Base Rate (€${venue.pricePerNight} × ${nights} nights)`,
      amount: basePriceTotal
    });

    if (!venue.priceIncludesUtilities && venue.utilities) {
      venue.selectedUtilities.forEach(utilId => {
        const utilConfig = venue.utilities.find(u => u.id === utilId);
        if (!utilConfig) return;

        let currentUtilCost = 0;
        if (utilConfig.priceType === 'per_booking') {
          const period = utilConfig.periods.find(p => 
            isWithinInterval(startDate, { start: parseISO(p.startDate), end: parseISO(p.endDate) })
          ) || utilConfig.periods[0];
          currentUtilCost = parseFloat(period?.costPerDay || 0);
          breakdownItems.push({
            label: utilConfig.name,
            amount: currentUtilCost
          });
        } else {
          let dailySum = 0;
          daysInterval.forEach(day => {
            const period = utilConfig.periods.find(p => 
              isWithinInterval(day, { start: parseISO(p.startDate), end: parseISO(p.endDate) })
            ) || utilConfig.periods[0];
            dailySum += parseFloat(period?.costPerDay || 0);
          });
          currentUtilCost = dailySum;
          breakdownItems.push({
            label: `${utilConfig.name} (Utilities)`,
            amount: currentUtilCost
          });
        }
        utilitiesTotal += currentUtilCost;
      });
    }

    const serviceFee = Math.round((basePriceTotal + utilitiesTotal) * 0.03);
    breakdownItems.push({
      label: 'Service Fee (3%)',
      amount: serviceFee
    });

    const grandTotal = basePriceTotal + utilitiesTotal + serviceFee;
    
    setPricingBreakdown({
      subtotal: basePriceTotal,
      utilities: utilitiesTotal,
      serviceFee,
      total: grandTotal,
      items: breakdownItems,
      nights
    });
  };

  const handlePaymentSuccess = () => {
    setStep(4);
    window.scrollTo(0, 0);
  };

  const handleBankTransferSubmit = () => {
    setStep(4);
    window.scrollTo(0, 0);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  if (!venue) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-500">Loading venue details...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Logo iconClassName="h-6 w-6" textClassName="text-lg" />
            </div>
            <div className="text-sm text-gray-500 flex items-center">
              <SafeIcon icon={FiLock} className="mr-1 h-3 w-3" />
              Secure Booking
            </div>
          </div>
        </div>
      </nav>
      {/* Rest of the component code (same as before) */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            {step === 1 && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="relative h-64 md:h-96">
                  <img src={venue.photos[0]} alt={venue.name} className="w-full h-full object-cover" />
                  <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur px-4 py-2 rounded-lg shadow-sm">
                    <h1 className="text-2xl font-bold text-gray-900">{venue.name}</h1>
                    <div className="flex items-center text-gray-600 mt-1">
                      <SafeIcon icon={FiMapPin} className="h-4 w-4 mr-1" />
                      <span className="text-sm">{venue.location.address}</span>
                    </div>
                  </div>
                </div>
                
                <div className="p-6 md:p-8">
                  <div className="flex flex-wrap gap-6 mb-8 pb-6 border-b border-gray-100">
                    <div className="flex items-center text-gray-700">
                      <SafeIcon icon={FiUsers} className="mr-2 h-5 w-5 text-blue-600" />
                      <span className="font-medium">{venue.maxGuests} Guests Max</span>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <SafeIcon icon={FiStar} className="mr-2 h-5 w-5 text-yellow-500" />
                      <span className="font-medium">{venue.rating} ({venue.reviews} reviews)</span>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <SafeIcon icon={FiCheck} className="mr-2 h-5 w-5 text-green-500" />
                      <span className="font-medium">Instant Confirmation</span>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-3">About this place</h3>
                      <p className="text-gray-600 leading-relaxed mb-6">{venue.description}</p>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-3">Amenities</h3>
                      <div className="grid grid-cols-1 gap-3">
                        {venue.facilities.map((facility, idx) => (
                          <div key={idx} className="flex items-center text-gray-600">
                            <div className="bg-blue-50 p-1.5 rounded-full mr-3">
                              <SafeIcon icon={FiCheck} className="h-3 w-3 text-blue-600" />
                            </div>
                            <span className="text-sm">{facility}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="bg-white rounded-2xl shadow-sm p-6 md:p-8">
                <button onClick={() => setStep(1)} className="text-gray-500 hover:text-gray-700 mb-6 flex items-center text-sm font-medium transition-colors">
                  <SafeIcon icon={FiArrowLeft} className="mr-2 h-4 w-4" />
                  Back to property
                </button>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Guest Information</h2>
                <form className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                      <input 
                        type="text" 
                        value={guestDetails.firstName}
                        onChange={(e) => setGuestDetails({...guestDetails, firstName: e.target.value})}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                      <input 
                        type="text" 
                        value={guestDetails.lastName}
                        onChange={(e) => setGuestDetails({...guestDetails, lastName: e.target.value})}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-all"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                    <input 
                      type="email" 
                      value={guestDetails.email}
                      onChange={(e) => setGuestDetails({...guestDetails, email: e.target.value})}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <input 
                      type="tel" 
                      value={guestDetails.phone}
                      onChange={(e) => setGuestDetails({...guestDetails, phone: e.target.value})}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Special Requests (Optional)</label>
                    <textarea 
                      rows="3" 
                      value={guestDetails.specialRequests}
                      onChange={(e) => setGuestDetails({...guestDetails, specialRequests: e.target.value})}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-all"
                    ></textarea>
                  </div>
                  <div className="pt-4">
                    <button 
                      type="button"
                      onClick={() => setStep(3)}
                      disabled={!guestDetails.firstName || !guestDetails.lastName || !guestDetails.email}
                      className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-8 py-3 rounded-lg font-bold transition-all shadow-md hover:shadow-lg"
                    >
                      Continue to Payment
                    </button>
                  </div>
                </form>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="bg-white rounded-2xl shadow-sm p-6 md:p-8">
                <button onClick={() => setStep(2)} className="text-gray-500 hover:text-gray-700 mb-6 flex items-center text-sm font-medium transition-colors">
                  <SafeIcon icon={FiArrowLeft} className="mr-2 h-4 w-4" />
                  Back to details
                </button>
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Payment Method</h2>
                  <p className="text-gray-600 mt-1">Choose how you want to pay.</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div 
                    onClick={() => setPaymentMethod('card')}
                    className={`cursor-pointer rounded-xl border-2 p-4 flex flex-col items-center justify-center transition-all ${
                      paymentMethod === 'card' 
                        ? 'border-blue-600 bg-blue-50 text-blue-700' 
                        : 'border-gray-200 hover:border-gray-300 text-gray-600'
                    }`}
                  >
                    <SafeIcon icon={FiCreditCard} className="h-8 w-8 mb-2" />
                    <span className="font-semibold">Credit Card</span>
                  </div>
                  <div 
                    onClick={() => setPaymentMethod('bank')}
                    className={`cursor-pointer rounded-xl border-2 p-4 flex flex-col items-center justify-center transition-all ${
                      paymentMethod === 'bank' 
                        ? 'border-blue-600 bg-blue-50 text-blue-700' 
                        : 'border-gray-200 hover:border-gray-300 text-gray-600'
                    }`}
                  >
                    <SafeIcon icon={FiBriefcase} className="h-8 w-8 mb-2" />
                    <span className="font-semibold">Bank Transfer</span>
                  </div>
                </div>

                {paymentMethod === 'card' && (
                  <MockStripePayment amount={pricingBreakdown?.total || 0} onSuccess={handlePaymentSuccess} />
                )}

                {paymentMethod === 'bank' && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                      <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                        <SafeIcon icon={FiBriefcase} className="mr-2 h-5 w-5 text-gray-500" />
                        Bank Details
                      </h3>
                      {bankDetails ? (
                        <div className="space-y-4 text-sm">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <p className="text-gray-500 text-xs uppercase tracking-wide">Account Holder</p>
                              <p className="font-medium text-gray-900">{bankDetails.accountHolder}</p>
                            </div>
                            <div>
                              <p className="text-gray-500 text-xs uppercase tracking-wide">Bank Name</p>
                              <p className="font-medium text-gray-900">{bankDetails.bankName}</p>
                            </div>
                            <div className="col-span-1 md:col-span-2 relative group cursor-pointer" onClick={() => copyToClipboard(bankDetails.iban)}>
                              <p className="text-gray-500 text-xs uppercase tracking-wide">IBAN</p>
                              <div className="flex items-center">
                                <p className="font-mono text-gray-900 font-medium">{bankDetails.iban}</p>
                                <SafeIcon icon={FiCopy} className="ml-2 h-4 w-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                            </div>
                            <div>
                              <p className="text-gray-500 text-xs uppercase tracking-wide">SWIFT / BIC</p>
                              <p className="font-mono text-gray-900 font-medium">{bankDetails.swift}</p>
                            </div>
                          </div>
                          {bankDetails.instructions && (
                            <div className="bg-blue-100 text-blue-800 p-3 rounded-lg text-xs mt-4">
                              <strong>Note:</strong> {bankDetails.instructions}
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-red-500 text-sm">Bank details are not available for this venue.</p>
                      )}
                    </div>
                    
                    <button 
                      onClick={handleBankTransferSubmit}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-bold text-sm transition-all shadow-md hover:shadow-lg"
                    >
                      Confirm Booking & View Receipt
                    </button>
                    <p className="text-center text-xs text-gray-500">
                      Your booking will be confirmed once the transfer is received.
                    </p>
                  </motion.div>
                )}
              </motion.div>
            )}

            {step === 4 && (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl shadow-sm p-12 text-center">
                <div className="w-24 h-24 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={FiCheck} className="h-12 w-12 text-green-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  {paymentMethod === 'bank' ? 'Booking Pending' : 'Booking Confirmed!'}
                </h2>
                <p className="text-gray-600 mb-8 max-w-md mx-auto">
                  {paymentMethod === 'bank' ? (
                    <>
                      Thank you, {guestDetails.firstName}. Your reservation is tentatively confirmed. Please complete the bank transfer of <strong>€{pricingBreakdown?.total}</strong> to finalize your booking.
                    </>
                  ) : (
                    <>
                      Thank you, {guestDetails.firstName}. Your reservation at <strong>{venue.name}</strong> has been confirmed. A receipt has been sent to {guestDetails.email}.
                    </>
                  )}
                </p>
                <div className="flex justify-center gap-4">
                  <Link to="/" className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-700 transition-colors">
                    <SafeIcon icon={FiArrowLeft} className="mr-2 h-4 w-4" />
                    Return to Home
                  </Link>
                </div>
              </motion.div>
            )}
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <span className="text-2xl font-bold text-gray-900">€{venue.pricePerNight}</span>
                    <span className="text-gray-500"> / night</span>
                  </div>
                </div>

                {step === 1 ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Check-in</label>
                        <div className="relative">
                          <SafeIcon icon={FiCalendar} className="absolute left-3 top-3 h-4 w-4 text-gray-500 z-10" />
                          <DatePicker
                            selected={startDate}
                            onChange={(date) => {
                              setStartDate(date);
                              if (endDate && date >= endDate) {
                                setEndDate(null);
                              }
                            }}
                            selectsStart
                            startDate={startDate}
                            endDate={endDate}
                            minDate={new Date()}
                            dateFormat="dd/MM/yyyy"
                            placeholderText="dd/mm/yyyy"
                            className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm w-full"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Check-out</label>
                        <div className="relative">
                          <SafeIcon icon={FiCalendar} className="absolute left-3 top-3 h-4 w-4 text-gray-500 z-10" />
                          <DatePicker
                            selected={endDate}
                            onChange={(date) => setEndDate(date)}
                            selectsEnd
                            startDate={startDate}
                            endDate={endDate}
                            minDate={startDate ? addDays(startDate, 1) : new Date()}
                            dateFormat="dd/MM/yyyy"
                            placeholderText="dd/mm/yyyy"
                            className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm w-full"
                            disabled={!startDate}
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Guests</label>
                      <div className="relative">
                        <select 
                          value={guests} 
                          onChange={(e) => setGuests(parseInt(e.target.value))}
                          className="w-full appearance-none bg-white border border-gray-300 rounded-xl px-4 py-3 pr-8 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {[...Array(venue.maxGuests)].map((_, i) => (
                            <option key={i + 1} value={i + 1}>{i + 1} guest{i > 0 && 's'}</option>
                          ))}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500">
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                        </div>
                      </div>
                    </div>

                    {pricingBreakdown && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="py-4 border-t border-gray-100 space-y-2">
                        {pricingBreakdown.items.map((item, index) => (
                          <div key={index} className="flex justify-between text-sm text-gray-600">
                            <span>{item.label}</span>
                            <span>€{item.amount}</span>
                          </div>
                        ))}
                        <div className="flex justify-between font-bold text-gray-900 pt-3 border-t border-gray-100 mt-2">
                          <span>Total</span>
                          <span>€{pricingBreakdown.total}</span>
                        </div>
                      </motion.div>
                    )}

                    <button 
                      onClick={() => setStep(2)}
                      disabled={!startDate || !endDate}
                      className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed text-white py-4 rounded-xl font-bold shadow-md transition-all transform hover:scale-[1.02] mt-2"
                    >
                      {startDate && endDate ? 'Reserve' : 'Check Availability'}
                    </button>
                    <div className="text-center text-xs text-gray-500 mt-2">
                      You won't be charged yet
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="bg-gray-50 rounded-xl p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold text-gray-900">Dates</p>
                          <p className="text-sm text-gray-600">{format(startDate, 'MMM d')} - {format(endDate, 'MMM d, yyyy')}</p>
                        </div>
                        {step < 4 && (
                          <button onClick={() => setStep(1)} className="text-blue-600 text-sm font-medium hover:underline">Edit</button>
                        )}
                      </div>
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold text-gray-900">Guests</p>
                          <p className="text-sm text-gray-600">{guests} guest{guests > 1 && 's'}</p>
                        </div>
                      </div>
                    </div>

                    {pricingBreakdown && (
                      <div className="space-y-3">
                        {pricingBreakdown.items.map((item, index) => (
                          <div key={index} className="flex justify-between text-sm text-gray-600">
                            <span>{item.label}</span>
                            <span>€{item.amount}</span>
                          </div>
                        ))}
                        <div className="border-t border-gray-200 pt-4 flex justify-between items-center">
                          <span className="font-bold text-gray-900">Total (EUR)</span>
                          <span className="font-bold text-xl text-gray-900">€{pricingBreakdown.total}</span>
                        </div>
                      </div>
                    )}

                    {step !== 4 && (
                      <div className="flex items-start gap-2 text-xs text-gray-500 bg-blue-50 p-3 rounded-lg border border-blue-100">
                        <SafeIcon icon={FiInfo} className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <p>Total includes taxes and fees. Cancellation is free for 48 hours after booking.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicBookingPage;